import { toast } from "react-hot-toast";

import { getConfig } from "../../config";
import { getToTransformer, getFromTransformer, TransformTypes } from "../../platform/backend/api-factory";
import { transformToBackendTenant, transformFromBackendTenant, ITenant } from "../../platform/backend/transformers/tenant-transformer";
import { transformFromBackendUser } from "../../platform/backend/transformers/user-transformer";
import { CustomToast } from "../components/custom-toast/custom-toast";
import { ITableRowData } from "../components/normal-table/normal-table";
import { IResponse } from "../hooks/use-http";
import { IObject } from "./utils";

export const failedToast = (msg?: string, unexpected?: string) =>
  toast(
    (t) => (
      <CustomToast
        t={t}
        type="error"
        message={msg ?? "The call has failed"}
      />
    ),
    {
      duration: unexpected ? 50000000 : 5000,
      position: "bottom-right",
      style: { maxWidth: "80%" }
    }
  );

export const warningToast = (msg: string) =>
  toast(
    (t) => (
      <CustomToast
        t={t}
        type="default"
        message={msg}
      />
    ),
    {
      duration: 8000,
      position: "bottom-right",
      style: { maxWidth: "80%" }
    }
  );

export const succesfulToast = (msg?: string) => {
  if (msg === "none" || msg === "The template has been selected") return;
  return toast(
    (t) => (
      <CustomToast
        t={t}
        type="success"
        message={msg ?? "Saved successfully"}
      />
    ),
    {
      duration: 2000,
      position: "bottom-right",
      style: { maxWidth: "80%" }
    }
  );
};

export interface IDetailPageData {
  rowData: ITableRowData[];
  rows?: ITableRowData[];
  name?: string;
  id?: string;
  archived?: boolean;
  [index: string]: any; // eslint-disable-line
}

export interface IDetailPage {
  [index: string]: string | IObject[];
}

export interface IListPageData {
  rows: ITableRowData[];
}

export interface IUsers {
  users: string[];
}

export interface IImport {
  record_id: string,
  record_type: string,
  source_type: string,
  name?: string,
  database_connection_id?: string,
  file?: File,
  csv_separator?: string
}

export interface IDeleteItemData {
  ids: string[];
}

export interface IRegisterData {
  email: string;
  password: string;
  name?:string;
  active_status?: string;
  last_name?: string;
}

export type IBackendType = "analytics" | "platform" | "manager"

export const typeIsIListPageData = (obj: any): obj is IListPageData => obj && obj.rows; // eslint-disable-line
export const typeIsIDeleteItemData = (obj: any): obj is IDeleteItemData => obj && obj.ids; // eslint-disable-line 

function getDefaultHeaders(tenant?: string, contentType?: string | undefined) {
  const base = {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": "true"
  };
  if ((contentType === "formPlatform" || contentType === "formDataset") && tenant) {
    return { ...base, "Content-Type": "multipart/form-data", "tenant-id": tenant };
  }
  else if (contentType !== "form" && tenant) {
    return { ...base, "Content-Type": "application/json", "tenant-id": tenant };
  }
  else if (tenant) {
    return { ...base, "tenant-id": tenant };
  }
  else if (!tenant) {
    return { ...base, "Content-Type": "application/json" };
  }
}
const defineUrl = (type: IBackendType = "platform") => {
  const { BASE_URL, BASE_URL_ANALYTICS, BASE_URL_MANAGER } = getConfig();
  switch (type) {
    case "analytics": return BASE_URL_ANALYTICS!;
    case "manager":   return BASE_URL_MANAGER!;
    case "platform":
    default:          return BASE_URL;
  }
};

export async function heartbeat(
): Promise<IResponse | undefined> {
  const response = await fetch(`${defineUrl()}heartbeat`, {
    method: "GET",
    headers: {
      ...getDefaultHeaders(),
    }
  });
  await response.json();

  if (!response.ok) {
    failedToast("Cannot connect to the backend", "true");
  }
  return undefined;
}

export async function authenticate(
  url: string,
  token: string,
  dataToBeSent: IDetailPageData | IDeleteItemData | IUsers| IRegisterData | string | undefined
): Promise<IResponse | undefined> {
  if (dataToBeSent) {
    const response = await fetch(`${defineUrl()}${url}`, {
      method: "GET",
      headers: {
        ...getDefaultHeaders(),
        "Authorization": `${dataToBeSent}`
      },
    });
    const data = await response.json();

    if (!response.ok) {
      failedToast(data.msg, data.unexpected);
      throw new Error(data.message || "The call failed");
    }
    return data;
  }
}

export async function getAllData(
  url: string,
  token: string,
  dataToBeSent?: IDetailPageData | IDeleteItemData | IUsers | IRegisterData | string,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string,
  successMsg?: string,
  errorMsg?: string
){
  if (token !== "") {
    const response = await fetch(`${defineUrl(backend)}${url}${dataToBeSent? dataToBeSent : ""}`, {
      headers: {
        ...getDefaultHeaders(tenant),
        "x-access-token" : token
      },
    });

    const data = await response.json();
    if (!response.ok) {
      failedToast(errorMsg || data.msg, data.unexpected);
      throw new Error(errorMsg || data.reason || "The call has failed");
    }
    if (type === "trigger") {
      succesfulToast();
    } else if (successMsg === "Loaded succesfully") {
      succesfulToast(successMsg);
    }

    if (type) {
      return getFromTransformer(type)(data);
    }

    return {
      rows: data
    };
  }
}

export async function getData(
  url: string,
  token: string,
  tenantId: string,
  type: TransformTypes,
  backend: IBackendType
) : Promise<IDetailPage> {
  const response = await fetch(`${defineUrl(backend)}${url}`, {
    headers: {
      ...getDefaultHeaders(tenantId),
      "x-access-token" : token
    }
  });

  const data = await response.json();

  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  
  return getFromTransformer(type)(data);
}

export async function getAllDataNonTable(
  url: string,
  token: string,
  dataToBeSent?: ITenant,
  type?: TransformTypes | undefined,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string,
  successMsg?: string
){
  if (token !== "") {
    const response = await fetch(`${defineUrl(backend)}${url}${dataToBeSent? dataToBeSent : ""}`, {
      headers: {
        ...getDefaultHeaders(tenant),
        "x-access-token" : token
      },
    });

    if ((type === "model" || type === "document" ) && response.ok) {
      succesfulToast(successMsg);
      const res = await response.blob();
      return res;
    } else if (successMsg === "Loaded succesfully") {
      succesfulToast(successMsg);
    } else if (successMsg === "Template selected successfully") {
      succesfulToast(successMsg);
    }

    if (!response.ok) {
      const data = await response.json();
      failedToast(data.msg, data.unexpected);
      throw new Error(data.reason || "The call failed");
    }
    
    try {
      const data = await response.text();
      const dataJson = JSON.parse(data);
      if (type) {
        return getFromTransformer(type)(dataJson);
      }
    
      return dataJson;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      throw error;
    }
  }
}

export async function downloadFile(
  url: string,
  token: string,
  dataToBeSent?: ITenant,
  type?: TransformTypes | undefined,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string,
  successMsg?: string
){
  if (token !== "") {
    const response = await fetch(`${defineUrl(backend)}${url}${dataToBeSent? dataToBeSent : ""}`, {
      headers: {
        ...getDefaultHeaders(tenant),
        "x-access-token" : token
      },
    });

    if (response.ok) {
      const res = await response.blob();

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      return {
        file: res,
        filename: filename
      };
    } else if (successMsg === "Loaded succesfully") {
      succesfulToast(successMsg);
    } else if (successMsg === "Template selected successfully") {
      succesfulToast(successMsg);
    }

    if (!response.ok) {
      const data = await response.json();
      failedToast(data.msg, data.unexpected);
      throw new Error(data.reason || "The call failed");
    }

    const dataJson = await response.json();
    if (type) {
      return getFromTransformer(type)(dataJson);
    }
    
    return dataJson;
  }
}

export async function downloadFilePost(
  url: string,
  token: string,
  dataToBeSent?: any, // eslint-disable-line
  type?: TransformTypes | undefined,
  backend?: IBackendType,
  tenant?: string,
) {
  if (token !== "") {
    const response = await fetch(`${defineUrl(backend)}${url}`, {
      method: "POST",
      body: JSON.stringify(dataToBeSent),
      headers: {
        ...getDefaultHeaders(tenant),
        "x-access-token": token,
      },
    });

    if (response.ok) {
      const res = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "export.csv";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      return { file: res, filename, truncated: response.headers.get("X-Export-Truncated") === "true" };
    }

    const data = await response.json();
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
}

export async function getUser(
  url: string,
  token: string,
  dataToBeSent?: IDetailPageData | IDeleteItemData | IUsers | IRegisterData | string,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string
) {
  if (token !== "") {
    const response = await fetch(`${defineUrl(backend)}${url}`, {
      headers: {
        ...getDefaultHeaders(tenant),
        "x-access-token" : token
      },
    });
    const data = await response.json();
    if (!response.ok) {
      failedToast(data.msg, data.unexpected);
      throw new Error(data.reason || "The call failed");
    }
    return transformFromBackendUser(data);
  }
}

export async function postSingleTenant(
  url: string,
  token: string,
  dataToBeSent?:  ITenant,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string
) {
  if (!dataToBeSent || !token) {
    return undefined;
  }
  const result = JSON.stringify(transformToBackendTenant(dataToBeSent));
  
  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "POST",
    body: result,
    headers: {
      ...getDefaultHeaders(tenant),
      "x-access-token" : token
    },
  });
  const data = await response.json();

  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  succesfulToast();
  return transformFromBackendTenant(data);
}

export async function postSingleNonTableImport(
  url: string,
  token: string,
  dataToBeSent: IImport | any, // eslint-disable-line
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string
) {
  if (!dataToBeSent || !token) {
    return undefined;
  }
  
  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "POST",
    body: extraType ? dataToBeSent : JSON.stringify(dataToBeSent),
    headers: {
      ...getDefaultHeaders(tenant, extraType),
      "x-access-token" : token
    },
  });

  const data = await response.json();

  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  
  return getFromTransformer(type || "default")(data);
}

export async function postSingleNonTable(
  url: string,
  token: string,
  dataToBeSent?: any, // eslint-disable-line
  type?: TransformTypes | TransformTypes,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string,
  successMsg?: string
) {
  if (!dataToBeSent || !token) {
    return undefined;
  }

  let result = undefined;

  if (extraType === "formPlatform") {
    const formData = new FormData();
    formData.append("id", dataToBeSent?.id);
    formData.append("context", dataToBeSent.context);
    result = formData;
  } else if (extraType === "formDataset") {
    const formData = new FormData();
    formData.append("access_type", dataToBeSent?.access_type);
    formData.append("name", dataToBeSent?.name);
    if (dataToBeSent?.tenant_id) {
      formData.append("tenant_id", dataToBeSent.tenant_id);
    }
    if (dataToBeSent?.id) {
      formData.append("id", dataToBeSent?.id);
    }
    if (dataToBeSent?.context) {
      formData.append("context", dataToBeSent?.context);
    }
    result = formData;
  } else {
    result = JSON.stringify(dataToBeSent);
  }

  const extraTypeLoc =  extraType === "formPlatform" || extraType === "formDataset"? "form" : extraType;

  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "POST",
    body: result,
    headers: {
      ...getDefaultHeaders(tenant, extraTypeLoc),
      "x-access-token" : token
    },
  });
  if (type === "pdf" && response.ok) {
    succesfulToast();
    const disposition = response.headers.get("Content-Disposition");
    const filename = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]?.replace(/['"]/g, "") ?? "report.pdf";
    const blob = await response.blob();
    return { blob, filename };
  }
  const data = await response.json();
  
  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  succesfulToast(data.msg ? data.msg : successMsg);
  return data; 
}

export async function postSingleDataset(
  url: string,
  token: string,
  dataToBeSent?: IDetailPageData | IDeleteItemData | IUsers | IRegisterData | string,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string,
  successMsg?: string
) {
  if (!type || !dataToBeSent || !token) {
    return undefined;
  }

  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "POST",
    body: JSON.stringify(dataToBeSent),
    headers: {
      ...getDefaultHeaders(tenant),
      "x-access-token" : token
    },
  });
  const data = await response.json();
  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  succesfulToast(successMsg);
  return data;
}

export async function postSingle(
  url: string,
  token: string,
  dataToBeSent?: IDetailPageData | IDeleteItemData | IUsers | IRegisterData | string,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string,
  extraType?: string,
  successMsg?: string
) {
  if (!type || !dataToBeSent || !token) {
    return undefined;
  }
  const transform = getToTransformer(type);

  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "POST",
    body: JSON.stringify(transform?.(dataToBeSent) ?? {}),
    headers: {
      ...getDefaultHeaders(tenant),
      "x-access-token" : token
    },
  });
  const data = await response.json();
  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.msg || "The call failed");
  }
  succesfulToast(successMsg);
  
  if (type) {
    return getFromTransformer(type)(data);
  }
  return data;
}

export async function deleteMultiple(
  url: string,
  token: string,
  dataToBeSent?: IDetailPageData | IDeleteItemData | IRegisterData | IUsers | string,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string
) {
  if (!token || !dataToBeSent || !typeIsIDeleteItemData(dataToBeSent)) {
    return undefined;
  }
  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "DELETE",
    body: JSON.stringify(dataToBeSent),
    headers: {
      ...getDefaultHeaders(tenant),
      "x-access-token" : token
    },
  });
  const data = await response.json();

  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  
  if (type == "archived"){
    succesfulToast("Deleted/Archived Successfully");
  } else { 
    succesfulToast("Deleted Successfully");
  }

  if (type) {
    return getFromTransformer(type)(data);
  }

  return {
    rows: data
  };
}

export async function postMultiple(
  url: string,
  token: string,
  dataToBeSent: IObject[] | undefined,
  type?: TransformTypes,
  backend?: IBackendType,
  tenant?: string,
) {
  if (!type || !dataToBeSent || !token) {
    return undefined;
  }

  const transform = getToTransformer(type);

  const response = await fetch(`${defineUrl(backend)}${url}`, {
    method: "POST",
    body: JSON.stringify(transform?.(dataToBeSent) ?? {}),
    headers: {
      ...getDefaultHeaders(tenant),
      "x-access-token" : token
    },
  });
  const data = await response.json();
  if (!response.ok) {
    failedToast(data.msg, data.unexpected);
    throw new Error(data.reason || "The call failed");
  }
  succesfulToast();
  
  if (type) {
    return getFromTransformer(type)(data);
  }
  return data;
}
